import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../../lib/supabase";

type Mode = "signin" | "setup" | "recovery";

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recoverySent, setRecoverySent] = useState(false);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword({ email, password });
      if (authError) { setError("Email o contraseña incorrectos."); return; }
      if (!data.user) { setError("No se pudo iniciar sesión."); return; }
      const { data: adminRow } = await supabase.from("admins").select("email").maybeSingle();
      if (!adminRow) { await supabase.auth.signOut(); setError("Este email no tiene permisos de administrador."); return; }
      navigate("/admin/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) { setError("La contraseña debe tener al menos 8 caracteres."); return; }
    setLoading(true);
    try {
      const { data, error: signUpError } = await supabase.auth.signUp({ email, password });
      if (signUpError) {
        if (signUpError.message.includes("already registered")) {
          setError("Este email ya tiene una cuenta. Usa la pestaña \"Entrar\".");
        } else {
          setError(signUpError.message);
        }
        return;
      }
      if (!data.user) { setError("No se pudo crear la cuenta."); return; }
      const { data: adminRow } = await supabase.from("admins").select("email").maybeSingle();
      if (!adminRow) { await supabase.auth.signOut(); setError("Este email no está registrado como administrador."); return; }
      navigate("/admin/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleRecovery = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const redirectTo = `${window.location.origin}/admin/reset-password`;
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
      if (resetError) { setError("No se pudo enviar el email. Comprueba la dirección."); return; }
      setRecoverySent(true);
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (m: Mode) => { setMode(m); setError(null); setRecoverySent(false); };

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col items-center justify-center px-4">
      <meta name="robots" content="noindex, nofollow" />
      <div className="w-full max-w-sm">
        <div className="flex justify-center mb-8">
          <img
            src="https://static.readdy.ai/image/464d6b2d88c2c0b329c43db37f7a6b21/5e3d82f418168fbdbc49efd95d2ee821.png"
            alt="Auria"
            className="h-10 w-auto"
          />
        </div>

        <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
          {/* Header */}
          <div className="px-8 pt-8 pb-6 border-b border-stone-100">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-shield-check-fill text-amber-500 text-base" />
              </div>
              <span className="text-xs font-bold text-amber-600 uppercase tracking-widest">Acceso restringido</span>
            </div>
            <h1 className="text-xl font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
              {mode === "recovery" ? "Recuperar contraseña" : "Panel de administrador"}
            </h1>
            <p className="text-stone-400 text-sm mt-1">
              {mode === "recovery" ? "Te enviamos un enlace a tu email" : "Solo acceso autorizado"}
            </p>
          </div>

          {/* Tabs — only for signin/setup */}
          {mode !== "recovery" && (
            <div className="flex border-b border-stone-100">
              {(["signin", "setup"] as Mode[]).map((m) => (
                <button
                  key={m}
                  onClick={() => switchMode(m)}
                  className={`flex-1 py-3 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    mode === m
                      ? "text-stone-900 border-b-2 border-amber-500"
                      : "text-stone-400 hover:text-stone-600"
                  }`}
                >
                  {m === "signin" ? "Entrar" : "Primera vez"}
                </button>
              ))}
            </div>
          )}

          <div className="px-8 py-6">
            {/* RECOVERY MODE */}
            {mode === "recovery" && (
              recoverySent ? (
                <div className="text-center py-4 space-y-4">
                  <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-emerald-50 mx-auto">
                    <i className="ri-mail-check-line text-emerald-500 text-2xl" />
                  </div>
                  <div>
                    <p className="font-bold text-stone-900 text-sm mb-1">¡Email enviado!</p>
                    <p className="text-stone-500 text-xs leading-relaxed">
                      Revisa tu bandeja de entrada en <strong>{email}</strong>. Haz clic en el enlace para establecer tu nueva contraseña.
                    </p>
                  </div>
                  <button
                    onClick={() => switchMode("signin")}
                    className="text-amber-600 text-sm font-semibold hover:underline cursor-pointer whitespace-nowrap"
                  >
                    Volver al login
                  </button>
                </div>
              ) : (
                <form onSubmit={handleRecovery} className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-stone-600 uppercase tracking-wide mb-1.5">
                      Email de administrador
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setError(null); }}
                      placeholder="auriareceptionistai@gmail.com"
                      required
                      className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm text-stone-800 placeholder-stone-300 outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-50 transition-all"
                    />
                  </div>

                  {error && (
                    <div className="flex items-start gap-2 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                      <i className="ri-error-warning-line text-red-500 text-sm flex-shrink-0 mt-0.5" />
                      <p className="text-red-600 text-sm">{error}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading || !email}
                    className="w-full py-3 bg-stone-900 hover:bg-stone-800 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold text-sm rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <><i className="ri-loader-4-line animate-spin" /><span>Enviando...</span></>
                    ) : (
                      <><i className="ri-mail-send-line" /><span>Enviar enlace de recuperación</span></>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => switchMode("signin")}
                    className="w-full text-center text-stone-400 hover:text-stone-600 text-sm transition-colors cursor-pointer"
                  >
                    ← Volver al login
                  </button>
                </form>
              )
            )}

            {/* SIGNIN / SETUP MODES */}
            {mode !== "recovery" && (
              <form onSubmit={mode === "signin" ? handleSignIn : handleSetup} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-stone-600 uppercase tracking-wide mb-1.5">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(null); }}
                    placeholder="auriareceptionistai@gmail.com"
                    required
                    className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm text-stone-800 placeholder-stone-300 outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-50 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-stone-600 uppercase tracking-wide mb-1.5">
                    Contraseña {mode === "setup" && <span className="text-stone-400 font-normal normal-case">(mín. 8 caracteres)</span>}
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(null); }}
                    placeholder={mode === "setup" ? "Elige una contraseña segura" : "••••••••"}
                    required
                    className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm text-stone-800 placeholder-stone-300 outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-50 transition-all"
                  />
                </div>

                {error && (
                  <div className="flex items-start gap-2 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                    <i className="ri-error-warning-line text-red-500 text-sm flex-shrink-0 mt-0.5" />
                    <p className="text-red-600 text-sm">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || !email || !password}
                  className="w-full py-3 bg-stone-900 hover:bg-stone-800 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold text-sm rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <><i className="ri-loader-4-line animate-spin" /><span>{mode === "signin" ? "Verificando..." : "Creando cuenta..."}</span></>
                  ) : (
                    <><i className={mode === "signin" ? "ri-login-box-line" : "ri-user-add-line"} /><span>{mode === "signin" ? "Entrar al panel" : "Crear mi acceso admin"}</span></>
                  )}
                </button>

                {mode === "signin" && (
                  <button
                    type="button"
                    onClick={() => switchMode("recovery")}
                    className="w-full text-center text-stone-400 hover:text-amber-600 text-xs transition-colors cursor-pointer"
                  >
                    ¿Olvidaste tu contraseña?
                  </button>
                )}

                {mode === "setup" && (
                  <div className="p-3 bg-amber-50 border border-amber-100 rounded-xl">
                    <p className="text-amber-700 text-xs leading-relaxed">
                      <strong>Solo primera vez:</strong> Esta opción crea tu cuenta de acceso admin. Tu email ya está registrado como administrador autorizado.
                    </p>
                  </div>
                )}
              </form>
            )}
          </div>
        </div>

        <p className="text-center text-stone-400 text-xs mt-6">
          Auria · Panel privado de administrador
        </p>
        <a
          href="/"
          className="block text-center text-stone-400 hover:text-stone-600 text-xs mt-2 transition-colors cursor-pointer"
        >
          ← Volver a la web
        </a>
      </div>
    </div>
  );
}
